import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import type { ColDef } from '@/grid/types';
import type { OnFarmMatingRecord } from '@/services/on-farm-implant';
import { formatDateString } from '@/utils/formatting';

export type FreshMatingRow = OnFarmMatingRecord & {
  createdCount: number;
  queuedCount: number;
  discardedCount: number;
};

export const useFreshMatingColumnDefs = (): ColDef<FreshMatingRow>[] => {
  const { t } = useTranslation();

  return useMemo<ColDef<FreshMatingRow>[]>(
    () => [
      {
        colId: 'select',
        headerName: '',
        width: 44,
        maxWidth: 44,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        suppressMovable: true,
        lockPosition: true,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'expand',
        headerName: '',
        cellRenderer: 'agGroupCellRenderer',
        width: 36,
        maxWidth: 36,
        suppressMovable: true,
        lockPosition: true,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
      },
      {
        field: 'recordName',
        headerName: t('onFarmImplant.fresh.name'),
        flex: 1.2,
        minWidth: 140,
        cellClass: 'font-semibold',
        valueGetter: (params) => params.data?.recordName ?? params.data?.id,
      },
      {
        field: 'donorName',
        headerName: t('onFarmImplant.fresh.donor'),
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'sireName',
        headerName: t('onFarmImplant.fresh.sire'),
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'ranchId',
        headerName: t('onFarmImplant.fresh.ranchId'),
        width: 120,
      },
      {
        field: 'date',
        headerName: t('onFarmImplant.fresh.fertDate'),
        width: 110,
        valueFormatter: ({ value }) =>
          value ? formatDateString(value, 'MM-dd-yyyy') : '',
      },
      {
        colId: 'plate',
        headerName: t('onFarmImplant.fresh.allocation.plate'),
        width: 90,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => params.data?.allocation?.plate,
      },
      {
        colId: 'priority',
        headerName: t('onFarmImplant.fresh.allocation.priority'),
        width: 100,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => params.data?.allocation?.priority,
      },
      {
        field: 'createdCount',
        headerName: t('onFarmImplant.fresh.created'),
        width: 100,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'queuedCount',
        headerName: t('onFarmImplant.fresh.queued'),
        width: 100,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'discardedCount',
        headerName: t('onFarmImplant.fresh.discarded'),
        width: 110,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
      },
    ],
    [t],
  );
};
